// A list: its bar (Home, Find, Done or More), its heading, and its blocks of to-dos as the core's Lists arranges them,
// with the open card in place of its row and Quick Entry on top (ListScreen.swift, MacListView.swift).

import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { arrange, canShowCompleted, canSortDeadlinesFirst, laterLineIndex, placedByDate, TODAY_HABITS_ID, type ArchiveFilter, type ListArrangement, type ListSection, type Lists, type Route } from "../core/lists";
import { isList, type Project, type Section, type Todo } from "../core/records";
import { congratulationID } from "../core/birthdays";
import { inboxFigures, inboxStays } from "../core/inboxHistory";
import { key, t } from "../i18n";
import { AreaIconButton } from "./AreaIcon";
import { useWindow } from "./context";
import { bothDropProps, groundDropProps, headerDropProps, placeAfter, placeDragProps, placeDropProps } from "./dnd";
import { birthdayLabel, dayHeading, firstWeekday, listText, longDate, monthTitle, titleDate, routeIcon, routeTitle, shortDate, showsPlace, splitEmoji, syncLabel, syncTitle } from "./format";
import { Icon } from "./Icon";
import { ListHeader } from "./ListHeader";
import { Markdown } from "./Markdown";
import { Menu, Popover, type MenuItem } from "./Menu";
import type { SyncStatus } from "../core/syncStatus";
import { arrangementFor, completedKind, completedShown, deadlinesFirst, setListPref, showsCompleted, usePrefs, type Prefs, setWeekPref, setPrefs } from "./prefs";
import { QuickEntryCard } from "./QuickEntryCard";
import { DayView, offersOverview, ProjectGlance, TodayGlance, TodayViewSwitch, useShowsDay } from "./DayPages";
import { ProjectMark } from "./Sidebar";
import { SharedWithLine } from "./Sharing";
import { TemplatePage, TemplatesPage } from "./Templates";
import { AreaHabits, HabitPage, HabitsPage, openNewHabit } from "./Habits";
import { TodoCard } from "./TodoCard";
import { TodoRow } from "./TodoRow";
import { ScheduledControls, WeekBoard } from "./Week";

/**
 * The blocks a list shows, each arranged as the list is set (docs/55, item 2): open to-dos in the user's order, then
 * those that start later where the list puts them last, then the completed ones where the list shows them (Today
 * those done today unless hidden, a project once asked; the Archive its own). A just-completed to-do lingers in its
 * place for a moment.
 */
export function listSections(route: Route, lists: Lists, prefs: Prefs): ListSection[] {
  const sections = lists.sections(route, completedShown(route, prefs));
  if (route.kind === "search" || route.kind === "archive" || route.kind === "stale" || route.kind === "scheduled" || route.kind === "due") return sections;
  const arrangement = arrangementFor(route, prefs);
  const now = new Date();
  return sections.map((section) => ({ ...section, todos: arrangeKeepingLingering(section.todos, arrangement, lists, now) }));
}

/**
 * Today's empty sections step aside (Lists, TodayLayout.showsEmptySections). On the web they come back while to-dos or
 * sections are dragged (`body.dragging`), as places to let go: the web has no menu that moves a to-do into a Today
 * section, which the apps' editor and menus have. Only while Today shows its own sections (not grouped by project).
 */
function withDragOnlyTodaySections(sections: ListSection[], lists: Lists): { sections: ListSection[]; dragOnly: ReadonlySet<string> } {
  const none: ReadonlySet<string> = new Set();
  if (sections.some((section) => section.header.kind !== "none" && section.header.kind !== "section" && section.id !== TODAY_HABITS_ID)) return { sections, dragOnly: none };
  const shown = new Map(sections.flatMap((section) => (section.header.kind === "section" ? [[section.id, section] as const] : [])));
  const hidden = lists.todaySections.filter((section) => !shown.has(section.id));
  if (hidden.length === 0) return { sections, dragOnly: none };
  const own = lists.todaySections.map((today): ListSection => shown.get(today.id) ?? { id: today.id, header: { kind: "section", section: today }, todos: [], projects: [] });
  return { sections: [...sections.filter((section) => section.header.kind !== "section"), ...own], dragOnly: new Set(hidden.map((section) => section.id)) };
}

/** `arrange`, with the just-completed to-dos kept among the open ones (they leave their place only once they are gone). */
function arrangeKeepingLingering(todos: readonly Todo[], arrangement: ListArrangement, lists: Lists, now: Date): Todo[] {
  const byID = new Map(todos.map((todo) => [todo.id, todo]));
  const asOpen = todos.map((todo) => (todo.completedAt !== undefined && lists.isLingering(todo.id) ? { ...todo, completedAt: undefined } : todo));
  return arrange(asOpen, arrangement, lists.today, now).map((todo) => byID.get(todo.id) ?? todo);
}

/** How many completed to-dos a list would show, were it asked to (Store.completedCount): those that have already left their place. */
export function completedCount(route: Route, lists: Lists): number {
  if (!canShowCompleted(route)) return 0;
  return lists.sections(route, completedKind(route)).flatMap((section) => section.todos)
    .filter((todo) => todo.completedAt !== undefined && !lists.isLingering(todo.id)).length;
}

const EMPTY: Record<string, [string, string]> = {
  inbox: [key("Inbox Zero"), key("Everything has found its place.")],
  today: [key("Nothing for today"), key("Press Q to add a to-do, or drag one here.")],
  someday: [key("Nothing for someday"), key("Ideas you don't want to lose go here.")],
  stale: [key("Nothing stale"), key("To-dos untouched for a long time show up here.")],
  due: [key("No deadlines"), key("To-dos with a deadline show up here, the most urgent first.")],
  archive: [key("Nothing done yet"), key("Completed to-dos show up here, by the day you did them.")],
  scheduled: [key("Nothing scheduled"), key("Give a to-do a day and it waits here until then.")],
};

export function ListScreen({ sections, onToggleHome, onComplete }: { sections: ListSection[]; onToggleHome: () => void; onComplete: (ids: readonly string[]) => void }) {
  const window = useWindow();
  const { state, ui, set, layout, model, week } = window;
  const { lists } = state;
  const route = ui.route;
  const editing = ui.open !== null || ui.composing !== null;
  const searching = ui.finding && ui.query.trim() !== "";
  const findInput = useRef<HTMLInputElement>(null);

  // In the same task as the key that opened Find, so the next key already goes into the field (the Mac's type-to-find
  // lost letters typed before its field had the keyboard, 2026-10-22).
  useLayoutEffect(() => {
    if (ui.finding) findInput.current?.focus();
  }, [ui.finding]);

  const prefs = usePrefs();
  const project = route.kind === "project" ? lists.project(route.id) : undefined;
  const area = route.kind === "area" ? lists.area(route.id) : undefined;
  const total = sections.reduce((count, section) => count + section.todos.length + section.projects.length, 0);
  const inToday = route.kind === "today";
  const templatesPage = !searching && (route.kind === "templates" || route.kind === "template");
  // Habits (docs/63): its own page, and a habit's page in the content area, its open card grown (the sidebar stays).
  const habitsPage = !searching && route.kind === "habits";
  const habitPage = !searching ? ui.habitPage : null;
  const showsDay = useShowsDay(route) && !searching;
  // Home hidden in a wide window with Overview on (O): Today's or the project's overview beside the list (docs/51).
  // Not a board, a list that goes on, or the birthdays. Off, or narrower, the list alone. The Day view puts its two
  // halves side by side wherever it has the room (DayPages.tsx).
  const pages = prefs.showsOverview && !layout.narrow && !layout.medium && ui.sidebarHidden && !searching && !showsDay
    && (inToday || (project !== undefined && project.layout !== "board" && !isList(project) && project.kind !== "birthdays"));
  const arrangement = arrangementFor(route, prefs);
  // A project as a board fills the window under its header and scrolls both ways itself, so its horizontal scroll bar
  // sits at the window's bottom edge, always in reach (Bastian, 2026-10-29), not under the longest column.
  const board = !week && !searching && !habitsPage && !habitPage && !templatesPage && project?.layout === "board";
  // Completed to-dos, one click away at the end of the list (docs/55, items 1 and 15): only where the list can show
  // them, and only once there are some. When nothing else is left, the list says All Done and offers them there.
  const completed = searching || templatesPage || habitsPage || habitPage || week ? 0 : completedCount(route, lists);
  const shows = showsCompleted(route, prefs);
  const allDone = completed > 0 && !shows && total === 0 && ui.composing === null && !(area && lists.projectsIn(area).length);
  const toggleCompleted = () => setListPref(route, { showsCompleted: !shows });
  const rowsOf = inToday && !searching ? withDragOnlyTodaySections(sections, lists) : { sections, dragOnly: new Set<string>() };

  const closeCards = () => {
    // A click beside the open card closes it (the card saves on its own way out).
    document.dispatchEvent(new CustomEvent("todolix:close-card"));
  };

  return (
    <main className="main" {...groundDropProps()} onClick={(event) => {
      const target = event.target as HTMLElement;
      if (target.closest(".row, .card, button, input, textarea, .popover, a, .board-column")) return;
      if (editing) closeCards();
      else if (ui.selected.length) set({ selected: [] });
    }}>
      <div className="list-bar">
        <button className="glass toggle" aria-label={layout.medium ? t("Show Home") : t("Show or Hide Home")} title={`${t("Show or Hide Home")} (\\)`} onClick={onToggleHome}>
          <Icon name="sidebar" />
        </button>
        <button className="back" onClick={onToggleHome}><Icon name="chevLeft" />{t("Home")}</button>
        {ui.finding && (
          <label className="find">
            <Icon name="search" />
            <input ref={findInput} value={ui.query} placeholder={t("Find to-dos, projects, tags")} autoComplete="off" aria-label={t("Find")}
              onChange={(event) => set({ query: event.target.value, selected: [] })}
              onKeyDown={(event) => {
                if (event.key === "Escape") { event.preventDefault(); set({ finding: false, query: "" }); }
                if (event.key === "ArrowDown" && window.visibleIDs[0]) {
                  event.preventDefault();
                  const first = window.visibleIDs[0];
                  set({ selected: [first] });
                  requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-row="${first}"]`)?.focus());
                }
              }} />
            <span className="kbd find-kbd">Esc</span>
          </label>
        )}
        {/* Today's progress in the bar's middle only on Today; other lists show nothing there (the display decisions of
            2026-10-29, point 14 B). */}
        {!ui.finding && route.kind === "today" && !habitPage && <TodayProgressLine />}
        <span className="spacer" />
        {state.sync && <SyncCloud status={state.sync} />}
        {editing ? (
          <button className="mint-button" onClick={closeCards}>{t("Done")}</button>
        ) : (
          <ListMenu route={route} project={project} />
        )}
      </div>
      <div className={`list-body${editing ? " editing" : ""}${week ? " week-body" : ""}${board ? " board-body" : ""}${pages ? " paged" : ""}`}>
        {habitPage ? (
          <HabitPage key={habitPage} id={habitPage} />
        ) : habitsPage ? (
          <HabitsPage />
        ) : templatesPage ? (
          route.kind === "template" ? <TemplatePage id={route.id} /> : <TemplatesPage />
        ) : (
          <>
            {searching ? (
              <h1 className="screen-title found-title list-heading">{t("{count} found", { count: total })}</h1>
            ) : (
              <ListHeading route={route} project={project} />
            )}
            {route.kind === "today" && !searching && <Birthdays />}
            {ui.composing !== null && ui.composing.sectionID === undefined && ui.composing.after === undefined && !(week && ui.composing.day !== undefined) && (
              <QuickEntryCard route={searching ? { kind: "inbox" } : route} composing={ui.composing} />
            )}
            {route.kind === "archive" && !searching && <ArchiveFilterBar />}
            {area && !searching && <AreaHabits areaID={area.id} route={route} />}
            {area && !searching && <AreaProjects areaID={area.id} />}
            {week ? (
              <WeekBoard week={week} onComplete={onComplete} />
            ) : allDone ? (
              <AllDone count={completed} onShow={toggleCompleted} />
            ) : total === 0 && ui.composing === null && !(area && lists.projectsIn(area).length) && (
              <EmptyState route={searching ? { kind: "search", query: ui.query } : route} />
            )}
            {week ? null : project?.layout === "board" && !searching ? (
              <Board key={project.id} project={project} sections={sections} arrangement={arrangement} onComplete={onComplete} />
            ) : showsDay ? (
              <DayView sections={sections} onComplete={onComplete} />
            ) : pages ? (
              <div className="pages">
                {project ? <ProjectGlance project={project} /> : <TodayGlance />}
                <div className="rows" role="listbox" aria-multiselectable="true" aria-label={routeTitle(route, lists)}>
                  {rowsOf.sections.map((section) => (
                    <Block key={section.id} section={section} route={route} inToday={inToday} dragOnly={rowsOf.dragOnly.has(section.id)}
                      arrangement={arrangement} onComplete={onComplete} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="rows" role="listbox" aria-multiselectable="true" aria-label={searching ? t("Find") : routeTitle(route, lists)}>
                {rowsOf.sections.map((section) => (
                  <Block key={section.id} section={section} route={searching ? { kind: "search", query: ui.query } : route} inToday={inToday}
                    dragOnly={rowsOf.dragOnly.has(section.id)} arrangement={arrangement} onComplete={onComplete} />
                ))}
              </div>
            )}
            {completed > 0 && !allDone && (
              <div className="list-foot">
                <button className="foot-button" aria-pressed={shows} onClick={toggleCompleted}>
                  <Icon name={shows ? "eyeSlash" : "checkCircle"} />
                  {shows ? t("Hide Completed") : t("Show {count} Completed", { count: completed })}
                </button>
              </div>
            )}
            {route.kind === "archive" && !state.isPro && lists.hiddenHistoryCount > 0 && (
              <div className="banner history">
                <Icon name="archive" />
                <span>{t("{count} older to-dos are kept. Pro shows your whole history.", { count: lists.hiddenHistoryCount })}</span>
                <button className="link" onClick={() => window.showSheet({ kind: "pro", feature: "history" })}>{t("Learn More")}</button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function ListHeading({ route, project }: { route: Route; project?: Project }) {
  const { state } = useWindow();
  const { lists } = state;
  const showsDay = useShowsDay(route);
  if (project) {
    return (
      <ListHeader icon={<ProjectMark project={project} progress={lists.progress(project)} size={30} />} title={project.title}>
        <SharedWithLine route={route} />
        {project.notes && <div className="list-notes"><Markdown text={project.notes} /></div>}
      </ListHeader>
    );
  }
  if (route.kind === "area") {
    const area = lists.area(route.id);
    const { name } = splitEmoji(area?.title ?? "");
    return (
      // The icon is a button: a popover to choose another (docs/61).
      <ListHeader icon={area && <AreaIconButton area={area} />} title={name}>
        <SharedWithLine route={route} />
      </ListHeader>
    );
  }
  const due = route.kind === "due";
  const controls = route.kind === "today" ? <TodayViewSwitch /> : route.kind === "scheduled" ? <ScheduledControls /> : undefined;
  // Today: its short date beside the title, on its baseline ("Today 29 Oct", "Heute 29.10."); the Day view keeps the full
  // date under the title (the display decisions of 2026-10-29, point 12 E).
  const inToday = route.kind === "today";
  return (
    <ListHeader icon={<span className={`icon-square${route.kind === "stale" ? " stale" : ""}${due ? " due" : ""}${inToday ? " today" : ""}`}><Icon name={routeIcon(route)} /></span>}
      title={routeTitle(route, lists)} controls={controls} detail={inToday && !showsDay ? titleDate(state.today) : undefined}>
      {inToday && showsDay && <p className="heading-line">{longDate(state.today)}</p>}
      {route.kind === "inbox" && <InboxFiguresLines />}
    </ListHeader>
  );
}

/**
 * The Inbox's figures under its title (Store.inboxLevel; core/inboxHistory.ts): how much waits and for how long, and on
 * a second, smaller line what came and went this week. Nothing while the Inbox is empty. No score, no Decide (the web
 * has none).
 */
function InboxFiguresLines() {
  const { state } = useWindow();
  const { records, lists, zone } = state;
  const figures = inboxFigures(inboxStays(records.todos, records.inboxTraces), new Date(), zone, { open: lists.count({ kind: "inbox" }), firstWeekday: firstWeekday() });
  if (!figures) return null;
  const items = t("{count} to decide", { count: figures.open });
  return (
    <>
      <p className="heading-line">{figures.waited !== undefined ? t("{items} · the oldest has waited {count} days", { items, count: figures.waited }) : items}</p>
      {figures.week && <p className="heading-line small">{t("This week: {new} new, {settled} settled", { new: figures.week.new, settled: figures.week.settled })}</p>}
    </>
  );
}

function EmptyState({ route }: { route: Route }) {
  const words = route.kind === "search"
    ? [key("Nothing found"), key("Try a different word, or a tag with #.")]
    : EMPTY[route.kind] ?? [key("Nothing here"), key("Press Q to add a to-do.")];
  return (
    <div className="empty">
      <span className={`icon-square${route.kind === "today" ? " today" : ""}`}><Icon name={route.kind === "search" ? "search" : route.kind === "project" ? "list" : routeIcon(route)} /></span>
      <b>{t(words[0] ?? "")}</b>{t(words[1] ?? "")}
    </div>
  );
}

/** Only completed to-dos are left: say so, and show them from right here (AllDoneState). */
function AllDone({ count, onShow }: { count: number; onShow: () => void }) {
  return (
    <div className="empty all-done">
      <span className="icon-square tick"><Icon name="check" /></span>
      <b>{t("All Done")}</b>
      <button className="link" onClick={onShow}>{t("Show {count} Completed", { count })}</button>
    </div>
  );
}

/** The Archive narrowed to one project or area: a select the keyboard reaches, with a way back to everything. */
function ArchiveFilterBar() {
  const { state, ui, set, model } = useWindow();
  const { lists } = state;
  const filter = ui.archiveFilter;
  const value = filter ? `${filter.kind}:${filter.id}` : "";
  const choose = (next: ArchiveFilter | null) => {
    set({ archiveFilter: next });
    model.setArchiveFilter(next ?? undefined);
  };
  const areas = lists.sortedAreas;
  const loose = state.records.projects.filter((project) => project.completedAt === undefined && !areas.some((area) => area.id === project.areaID))
    .sort((a, b) => a.order - b.order);
  const name = (project: Project) => project.title;
  const areaName = (area: typeof areas[number]) => [splitEmoji(area.title).emoji, splitEmoji(area.title).name].filter(Boolean).join(" ");
  const chosen = filter?.kind === "project" ? lists.project(filter.id)?.title : filter?.kind === "area" ? lists.area(filter.id)?.title : undefined;
  return (
    <div className={`archive-filter${filter ? " on" : ""}`}>
      <label>
        <span className="archive-filter-label">{t("Show")}</span>
        <select className="field" value={value} aria-label={t("Narrow the Archive to a project or area")}
          onChange={(event) => {
            const [kind, id] = event.target.value.split(":");
            choose(kind === "project" || kind === "area" ? { kind, id: id ?? "" } : null);
          }}>
          <option value="">{t("Everything")}</option>
          {areas.map((area) => (
            <optgroup key={area.id} label={areaName(area)}>
              <option value={`area:${area.id}`}>{t("All of {name}", { name: areaName(area) })}</option>
              {lists.projectsIn(area).map((project) => <option key={project.id} value={`project:${project.id}`}>{name(project)}</option>)}
            </optgroup>
          ))}
          {loose.length > 0 && (
            <optgroup label={t("Projects")}>
              {loose.map((project) => <option key={project.id} value={`project:${project.id}`}>{name(project)}</option>)}
            </optgroup>
          )}
        </select>
      </label>
      {filter && chosen !== undefined && (
        <button className="small-button" onClick={() => choose(null)} aria-label={t("Show everything again")}>
          <Icon name="x" />{t("Everything")}
        </button>
      )}
    </div>
  );
}

/** An area's own page: its projects and lists above its loose to-dos. */
function AreaProjects({ areaID }: { areaID: string }) {
  const { state, go } = useWindow();
  const area = state.lists.area(areaID);
  if (!area) return null;
  const projects = state.lists.projectsIn(area).filter((project) => project.isOff !== true);
  if (projects.length === 0) return null;
  return (
    <div className="area-projects">
      {projects.map((project) => (
        <button key={project.id} className="row project-row" onClick={() => go({ kind: "project", id: project.id })}>
          <ProjectMark project={project} progress={state.lists.progress(project)} />
          <span className="title">{project.title}</span>
          <span className="side">{state.lists.openCount({ kind: "project", id: project.id }) || ""}</span>
        </button>
      ))}
    </div>
  );
}

/** One block of a list: its heading (a section, a day, a project, a text) and its rows. */
function Block({ section, route, inToday, dragOnly = false, arrangement, onComplete }: {
  section: ListSection; route: Route; inToday: boolean; dragOnly?: boolean; arrangement: ListArrangement; onComplete: (ids: readonly string[]) => void;
}) {
  const window = useWindow();
  const { state, ui, model, go } = window;
  const { lists } = state;
  const header = section.header;
  const later = laterLineIndex(section.todos, arrangement, lists.today);
  const rows = (
    <>
      {section.projects.map((project) => (
        <button key={project.id} className="row project-row" onClick={() => go({ kind: "project", id: project.id })}>
          <ProjectMark project={project} progress={lists.progress(project)} />
          <span className="title">{project.title}</span>
          <span />
        </button>
      ))}
      {section.todos.map((todo, index) => (
        <Fragment key={todo.id}>
          {index === later && <LaterLine />}
          {ui.open === todo.id ? <TodoCard todo={todo} /> : (
            <>
              <TodoRow todo={todo} route={route} showsPlace={showsPlace(route, header)} inToday={inToday} onComplete={onComplete} />
              {ui.composing?.after === todo.id && <QuickEntryCard route={route} composing={ui.composing} />}
            </>
          )}
        </Fragment>
      ))}
    </>
  );

  switch (header.kind) {
    case "none": case "general":
      return (
        <>
          {header.kind === "general" && <GroupHead title={t("Miscellaneous")} icon="misc" />}
          {rows}
        </>
      );
    case "section":
      // Let go on the heading, to-dos land at the section's top, under what it places by its dates.
      const shown = section.todos.filter((todo) => !placedByDate(todo, arrangement, lists.today)).map((todo) => todo.id);
      return <SectionBlock section={header.section} inToday={inToday} shown={shown} dragOnly={dragOnly}>{rows}</SectionBlock>;
    case "project":
      return (
        <div className="group">
          <button className="group-head" onClick={() => go({ kind: "project", id: header.project.id })}>
            <ProjectMark project={header.project} progress={lists.progress(header.project)} size={18} />
            <span>{header.project.title}</span>
          </button>
          {rows}
        </div>
      );
    case "area":
      return (
        <div className="group">
          <button className="group-head" onClick={() => go({ kind: "area", id: header.area.id })}><span>{header.area.title}</span></button>
          {rows}
        </div>
      );
    case "day": {
      const heading = dayHeading(header.day, lists.today);
      const birthdays = route.kind === "scheduled" ? lists.birthdaysOn(header.day) : [];
      const drop = route.kind === "scheduled" ? headerDropProps(window, (ids) => model.schedule(ids, { kind: "on", day: header.day })) : {};
      return (
        <div className="day-block">
          <div className="day-head" {...drop}><b>{heading.title}</b>{heading.detail && <span>{heading.detail}</span>}</div>
          {birthdays.map((birthday) => (
            <div key={birthday.id} className="birthday"><Icon name="gift" /><b>{birthday.name}</b></div>
          ))}
          {rows}
        </div>
      );
    }
    case "monthTitle":
      return <h2 className="month-title">{monthTitle(header.day, lists.today)}</h2>;
    case "month":
      return (
        <div className="day-block">
          <div className="day-head"><b>{monthTitle(header.day, lists.today)}</b></div>
          {lists.birthdaysInMonthFrom(header.day).map(({ birthday, day }) => (
            <div key={birthday.id} className="birthday"><Icon name="gift" /><b>{birthday.name}</b><span>{shortDate(day, lists.today)}</span></div>
          ))}
          {rows}
        </div>
      );
    case "text":
      return (
        <div className={`group${header.text === "habits" ? " habit-group" : ""}`}>
          {header.text === "habits" ? (
            <div className="text-head habit-group-head">
              <Icon name="flame" />{listText(header.text)}
              <span className="section-count">{t("{done} of {total}", { done: section.todos.filter((todo) => todo.completedAt !== undefined).length, total: section.todos.length })}</span>
            </div>
          ) : <div className={`text-head${header.text === "overdue" ? " red" : ""}`}>{listText(header.text)}</div>}
          {rows}
        </div>
      );
  }
}

/** Above the to-dos of a block that start on a later day: they come last, faded, with their date (docs/55, item 2). */
function LaterLine() {
  return <div className="later-line" role="separator" aria-label={t("Later")}>{t("Later")}</div>;
}

function GroupHead({ title, icon }: { title: string; icon: "misc" }) {
  return <div className="group-head static"><Icon name={icon} /><span>{title}</span></div>;
}

/**
 * Today's birthdays that have no to-do to congratulate this year (birthday to-dos off, or the to-do deleted): quiet rows
 * at the top of Today, without a box (Store.birthdaysWithoutTodo, the apps' BirthdayLine). A birthday with its to-do
 * says its words on that to-do's row (rowPlaceOf).
 */
function Birthdays() {
  const { state } = useWindow();
  const year = Number(state.today.slice(0, 4));
  const ids = new Set(state.records.todos.map((todo) => todo.id.toUpperCase()));
  const shown = state.lists.birthdaysOn(state.today).filter((birthday) => !ids.has(congratulationID(birthday.id, year)));
  if (shown.length === 0) return null;
  return (
    <div className="birthdays-today">
      {shown.map((birthday) => (
        <div key={birthday.id} className="birthday">
          <Icon name="gift" />
          <b>{birthday.name}</b>
          <span>{birthdayLabel(birthday, state.today)}</span>
        </div>
      ))}
    </div>
  );
}

/** A section of a project or of Today: fold, add a to-do into it, rename, delete; to-dos can be dropped on it. */
function SectionBlock({ section, inToday, shown, dragOnly = false, children }: { section: Section; inToday: boolean; shown: readonly string[]; dragOnly?: boolean; children: ReactNode }) {
  const window = useWindow();
  const count = shown.length;
  const { model, compose, showSheet } = window;
  const [renaming, setRenaming] = useState(false);
  const collapsed = section.isCollapsed === true;
  // In a list shared by someone else, its sections are the owner's: members fold them (for themselves) and add to them.
  const owns = section.projectID === undefined || (window.model.shareOf({ kind: "project", id: section.projectID })?.youOwn ?? true);
  const items: MenuItem[] = !owns ? [] : [
    { label: t("Rename"), run: () => setRenaming(true) },
    {
      label: t("Delete Section"), destructive: true,
      run: () => showSheet({
        kind: "confirm", title: t("Delete Section?"), action: t("Delete Section"),
        text: t("Its to-dos stay in the list, outside any section."), run: () => model.deleteSection(section.id),
      }),
    },
  ];
  return (
    <section className={`section${collapsed ? " collapsed" : ""}${dragOnly ? " drag-only" : ""}`} aria-label={section.title}>
      {/* To-dos let go on the heading land at the section's top, under it, where its line shows; also its own (the
          owner, 2026-10-19, as on the Mac). */}
      <div className="section-head" {...(owns ? placeDragProps(window, { kind: "section", id: section.id }) : {})}
        {...bothDropProps(headerDropProps(window, (ids) => model.moveToSectionTop(ids, section.id, shown, inToday), "drop-after"), placeDropProps({ kind: "section", id: section.id },
          // A section moves among its own list's sections; one shared by someone else is the owner's to move.
          (moving) => {
            const other = window.state.records.sections.find((candidate) => candidate.id === moving.id);
            return moving.kind === "section" && owns && other?.projectID === section.projectID ? "around" : null;
          },
          (moving, where) => {
            const siblings = window.state.records.sections.filter((other) => other.projectID === section.projectID).sort((a, b) => a.order - b.order);
            model.moveSection(moving.id, where === "before" ? section.id : placeAfter(siblings, section.id, moving.id));
          }))}>
        <button className="fold" onClick={() => model.updateSection({ ...section, isCollapsed: !collapsed })} aria-expanded={!collapsed}
          aria-label={collapsed ? t("Expand {name}", { name: section.title }) : t("Collapse {name}", { name: section.title })}>
          <Icon name="chevDown" className="chev" />
        </button>
        {renaming ? (
          <input className="section-title section-rename" defaultValue={section.title} autoFocus aria-label={t("Section Name")}
            onBlur={(event) => {
              const title = event.target.value.trim();
              if (title && title !== section.title) model.updateSection({ ...section, title });
              setRenaming(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") (event.target as HTMLInputElement).blur();
              if (event.key === "Escape") { event.preventDefault(); setRenaming(false); }
            }} />
        ) : (
          <h2 className="section-title" onDoubleClick={() => { if (owns) setRenaming(true); }}>{section.title}</h2>
        )}
        {collapsed && count > 0 && <span className="section-count">{count}</span>}
        <span className="spacer" />
        <button aria-label={t("Add To-Do to {name}", { name: section.title })} title={t("Add To-Do")} onClick={() => compose({ sectionID: section.id })}>
          <Icon name="plusThin" />
        </button>
        {items.length > 0 && <Menu label={t("More")} items={items} icon="more" />}
      </div>
      <div className="section-rows">
        {window.ui.composing?.sectionID === section.id && <QuickEntryCard route={window.ui.route} composing={window.ui.composing} />}
        {children}
      </div>
    </section>
  );
}

/** A project shown as a board: its sections side by side as columns (the to-dos outside any section first). */
function Board({ project, sections, arrangement, onComplete }: { project: Project; sections: ListSection[]; arrangement: ListArrangement; onComplete: (ids: readonly string[]) => void }) {
  const window = useWindow();
  const { ui, model, compose, state } = window;
  const route: Route = { kind: "project", id: project.id };
  return (
    <div className="board" role="group" aria-label={t("Board")}>
      {sections.map((section) => {
        const sectionID = section.header.kind === "section" ? section.header.section.id : undefined;
        if (sectionID === undefined && section.todos.length === 0) return null;
        const title = section.header.kind === "section" ? section.header.section.title : t("No Section");
        return (
          <div key={section.id} className="board-column">
            {/* The column's heading takes cards for its end: also its own cards go there (docs/59, item 2). */}
            <div className="board-head" {...headerDropProps(window, (ids) => model.moveToSectionEnd(ids, sectionID, section.todos.map((todo) => todo.id), false))}>
              <h2>{title}</h2>
              <span className="section-count">{section.todos.length}</span>
              <span className="spacer" />
              <button aria-label={t("Add To-Do to {name}", { name: title })} onClick={() => compose({ sectionID })}><Icon name="plusThin" /></button>
            </div>
            {ui.composing?.sectionID === sectionID && ui.composing !== null && sectionID !== undefined && <QuickEntryCard route={route} composing={ui.composing} />}
            {section.todos.map((todo, index) => (
              <Fragment key={todo.id}>
                {index === laterLineIndex(section.todos, arrangement, state.lists.today) && <LaterLine />}
                {ui.open === todo.id
                  ? <TodoCard todo={todo} />
                  : <TodoRow todo={todo} route={route} showsPlace={false} inToday={false} onComplete={onComplete} />}
              </Fragment>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** The list's More menu: a section, favorites (Pro), the board, Deadlines First where the list is in the user's order, the keyboard. */
function ListMenu({ route, project }: { route: Route; project?: Project }) {
  const window = useWindow();
  const { state, model, showSheet, compose } = window;
  const prefs = usePrefs();
  const items: MenuItem[] = [];
  // Habits' own, as the Mac's ⋯ (the display decisions of 2026-10-29, point 1 B): a new habit, the streak's number on
  // rows, and the rest of Habits' settings.
  if (route.kind === "habits") {
    items.push({ label: t("New Habit"), run: () => openNewHabit(window) });
    items.push({ label: t("Show Streaks"), checked: prefs.showsStreaks, run: () => setPrefs((current) => ({ ...current, showsStreaks: !current.showsStreaks })) });
    items.push({ label: t("Habits Settings…"), run: () => showSheet({ kind: "settings", section: "habits" }) });
  }
  // What is shared by someone else: its sections and layout are the owner's (the server refuses a member's change).
  const owns = route.kind !== "project" || (model.shareOf(route)?.youOwn ?? true);
  const share = route.kind === "project" || route.kind === "area" ? model.shareOf(route) : null;
  if (share) items.push({ label: t("People…"), run: () => showSheet({ kind: "people", route }) });
  if ((route.kind === "project" && owns) || route.kind === "today") {
    items.push({
      label: t("New Section"),
      run: () => {
        const section = model.addSection(t("New Section"), route.kind === "project" ? route.id : undefined);
        compose({ sectionID: section.id });
      },
    });
  }
  if (project) {
    const favorite = state.records.favorites.some((item) => item.kind === "project" && item.id === project.id);
    items.push({
      label: favorite ? t("Remove from Favorites") : t("Add to Favorites"),
      run: () => {
        if (!state.isPro) return showSheet({ kind: "pro", feature: "favorites" });
        model.setFavorites(favorite
          ? state.records.favorites.filter((item) => !(item.kind === "project" && item.id === project.id))
          : [...state.records.favorites, { kind: "project", id: project.id }]);
      },
    });
    if (!isList(project) && owns) {
      items.push({
        label: project.layout === "board" ? t("Show as List") : t("Show as Board"),
        run: () => model.updateProject({ ...project, layout: project.layout === "board" ? "list" : "board" }),
      });
    }
  }
  // Where mail to this list's own address goes: the Inbox, an area or a project (not the birthdays list).
  const area = route.kind === "area" ? state.lists.area(route.id) : undefined;
  if (route.kind === "inbox" || area || (project && project.kind !== "birthdays")) {
    items.push({
      label: t("Email Address…"),
      run: () => showSheet(route.kind === "inbox"
        ? { kind: "mail", mailKind: "inbox", label: t("Inbox") }
        : area ? { kind: "mail", mailKind: "area", targetID: area.id, label: area.title }
        : { kind: "mail", mailKind: "project", targetID: project?.id, label: project?.title ?? "" }),
    });
  }
  // Home hidden in a wide window: the overview beside the list (docs/51), also with O.
  if (offersOverview(route, project)) {
    const shows = prefs.showsOverview;
    items.push({ label: shows ? t("Hide Overview") : t("Show Overview"), run: () => setPrefs((current) => ({ ...current, showsOverview: !shows })) });
  }
  // Only where it changes something (docs/55, item 6): Deadlines sorts by deadline already, the Archive by the day done.
  if (canSortDeadlinesFirst(route)) {
    const on = deadlinesFirst(route, prefs);
    items.push({ label: t("Deadlines First"), checked: on, run: () => setListPref(route, { deadlinesFirst: !on }) });
  }
  // The week's rows with what they hold under the title, or the title alone (Bastian, 2026-10-20).
  if (route.kind === "scheduled" && prefs.week.shows) {
    const on = prefs.week.showsDetails;
    items.push({ label: t("Show Details"), checked: on, run: () => setWeekPref({ showsDetails: !on }) });
  }
  items.push({ label: t("Keyboard Shortcuts"), run: () => showSheet({ kind: "keys" }) });
  return <Menu label={t("More")} items={items} icon="more" className="glass" />;
}


/// How far today has come, in the middle of Today's bar: progress towards what was planned (TodayProgressLine).
/**
 * The cloud in the list's bar while this browser is not in step with the server (docs/64): a bare symbol in the
 * secondary colour before the list's menu, no background or border, a little apart from the glass buttons. A click
 * opens a popover with the detail and Try Again. It goes by itself, its popover with it, once the browser is in step.
 */
function SyncCloud({ status }: { status: SyncStatus }) {
  const { state, model } = useWindow();
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [trying, setTrying] = useState(false);
  const [stillBehind, setStillBehind] = useState(false);
  const now = Date.now();
  const label = syncLabel(status, now, state.zone);
  const explanation = status.kind === "notSynced"
    ? t("You're online, but Todolix can't reach its server. Your changes are kept in this browser and go up as soon as it can. Some networks, like a phone's hotspot, let other apps through but not Todolix. Another network often helps.")
    : status.kind === "offline" ? t("You're offline. Your changes are kept and sync when you're back.") : null;
  const tryAgain = async () => {
    setTrying(true);
    setStillBehind(false);
    // In step again: the cloud goes, and its popover with it.
    const inStep = await model.retrySync();
    setTrying(false);
    setStillBehind(!inStep);
  };
  return (
    <>
      <button ref={button} className="bare sync-cloud" aria-label={label} title={label} aria-expanded={open}
        onClick={() => { setOpen(!open); setStillBehind(false); }}>
        <Icon name="cloudOff" />
      </button>
      {open && (
        <Popover anchor={button} onClose={() => setOpen(false)} label={label} className="sync-popover">
          <h3>{syncTitle(status, now, state.zone)}</h3>
          {explanation && <p>{explanation}</p>}
          {status.kind === "update" ? (
            <button className="outline-button small" onClick={() => location.reload()}>{t("Update")}</button>
          ) : (
            <button className="outline-button small" aria-disabled={trying} onClick={() => { if (!trying) void tryAgain(); }}>{t("Try Again")}</button>
          )}
          {stillBehind && <p className="sync-still" role="status">{t("Still not reachable.")}</p>}
        </Popover>
      )}
    </>
  );
}

function TodayProgressLine() {
  const { state } = useWindow();
  const progress = state.lists.todayProgress;
  if (progress.total === 0) return null;
  return (
    <div className="bar-progress">
      <span className="bar-progress-label">{t("{done} of {total} done today", { done: progress.done, total: progress.total })}</span>
      <span className="bar-progress-track" aria-hidden="true">
        <span className="bar-progress-fill" style={{ width: `${(progress.done / progress.total) * 100}%`, minWidth: progress.done ? 3 : 0 }} />
      </span>
    </div>
  );
}
